import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CampaignWizard = ({ isOpen, onClose, onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    trigger: '',
    targetAudience: '',
    messages: [],
    schedule: {
      timezone: 'America/New_York',
      businessHours: true,
      respectHolidays: true
    }
  });

  const triggerOptions = [
    { value: 'birthday', label: 'Client Birthday' },
    { value: 'home_anniversary', label: 'Home Purchase Anniversary' },
    { value: 'post_closing', label: 'Post-Closing Follow-up' },
    { value: 'market_update', label: 'Market Update' },
    { value: 'seasonal', label: 'Seasonal Check-in' },
    { value: 'referral_request', label: 'Referral Request' }
  ];

  const audienceOptions = [
    { value: 'all_clients', label: 'All Active Clients' },
    { value: 'past_buyers', label: 'Past Buyers Only' },
    { value: 'past_sellers', label: 'Past Sellers Only' },
    { value: 'prospects', label: 'Prospects' },
    { value: 'high_value', label: 'High-Value Clients' },
    { value: 'custom', label: 'Custom Segment' }
  ];

  const messageTemplates = [
    {
      id: 1,
      name: "Birthday Greeting",
      content: "Happy Birthday, {{firstName}}! 🎉 Hope you have a wonderful day. As your real estate agent, I'm here if you need anything!",
      delay: 0
    },
    {
      id: 2,
      name: "Home Anniversary",
      content: "Happy {{anniversary}} anniversary in your home, {{firstName}}! Time flies! How are you enjoying the neighborhood?",
      delay: 0
    },
    {
      id: 3,
      name: "Market Update Follow-up",
      content: "Hi {{firstName}}, here's your personalized market update for {{neighborhood}}. Your home value has {{trend}}. Let me know if you have questions!",
      delay: 7
    }
  ];

  const steps = [
    { number: 1, title: 'Campaign Details', icon: 'FileText' },
    { number: 2, title: 'Trigger & Audience', icon: 'Target' },
    { number: 3, title: 'Message Sequence', icon: 'MessageSquare' },
    { number: 4, title: 'Schedule & Settings', icon: 'Clock' }
  ];

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = () => {
    onSave(campaignData);
    onClose();
  };

  const updateCampaignData = (field, value) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-4xl bg-card border border-border rounded-lg elevation-4 animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Create New Campaign</h2>
              <p className="text-sm text-muted-foreground">Set up automated messaging sequences for your clients</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => (
                <div key={step?.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    currentStep >= step?.number
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-border text-muted-foreground'
                  }`}>
                    <Icon name={step?.icon} size={16} />
                  </div>
                  <div className="ml-3 hidden md:block">
                    <div className={`text-sm font-medium ${
                      currentStep >= step?.number ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step?.title}
                    </div>
                  </div>
                  {index < steps?.length - 1 && (
                    <div className={`w-12 h-0.5 mx-4 ${
                      currentStep > step?.number ? 'bg-primary' : 'bg-border'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="p-6 min-h-[400px]">
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">Campaign Information</h3>
                  <div className="space-y-4">
                    <Input
                      label="Campaign Name"
                      placeholder="e.g., Birthday Wishes 2024"
                      value={campaignData?.name}
                      onChange={(e) => updateCampaignData('name', e?.target?.value)}
                      required
                    />
                    <Input
                      label="Description"
                      placeholder="Brief description of this campaign's purpose"
                      value={campaignData?.description}
                      onChange={(e) => updateCampaignData('description', e?.target?.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">Trigger & Target Audience</h3>
                  <div className="space-y-4">
                    <Select
                      label="Campaign Trigger"
                      description="What event should start this campaign?"
                      options={triggerOptions}
                      value={campaignData?.trigger}
                      onChange={(value) => updateCampaignData('trigger', value)}
                      placeholder="Select a trigger event"
                    />
                    <Select
                      label="Target Audience"
                      description="Which clients should receive this campaign?"
                      options={audienceOptions}
                      value={campaignData?.targetAudience}
                      onChange={(value) => updateCampaignData('targetAudience', value)}
                      placeholder="Select target audience"
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">Message Sequence</h3>
                  <div className="space-y-4">
                    {messageTemplates?.map((template, index) => (
                      <div key={template?.id} className="border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-primary">{index + 1}</span>
                            </div>
                            <div>
                              <h4 className="font-medium text-foreground">{template?.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {template?.delay === 0 ? 'Immediate' : `${template?.delay} days after trigger`}
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon">
                            <Icon name="Edit" size={16} />
                          </Button>
                        </div>
                        <div className="bg-muted/50 rounded p-3">
                          <p className="text-sm text-foreground">{template?.content}</p>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" className="w-full">
                      <Icon name="Plus" size={16} />
                      Add Message
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">Schedule & Settings</h3>
                  <div className="space-y-4">
                    <Select
                      label="Timezone"
                      options={[
                        { value: 'America/New_York', label: 'Eastern Time (ET)' },
                        { value: 'America/Chicago', label: 'Central Time (CT)' },
                        { value: 'America/Denver', label: 'Mountain Time (MT)' },
                        { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }
                      ]}
                      value={campaignData?.schedule?.timezone}
                      onChange={(value) => updateCampaignData('schedule', { ...campaignData?.schedule, timezone: value })}
                    />
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={campaignData?.schedule?.businessHours}
                          onChange={(e) => updateCampaignData('schedule', { ...campaignData?.schedule, businessHours: e?.target?.checked })}
                          className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                        />
                        <span className="text-sm text-foreground">Send only during business hours (9 AM - 6 PM)</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={campaignData?.schedule?.respectHolidays}
                          onChange={(e) => updateCampaignData('schedule', { ...campaignData?.schedule, respectHolidays: e?.target?.checked })}
                          className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                        />
                        <span className="text-sm text-foreground">Pause during major holidays</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              <Icon name="ChevronLeft" size={16} />
              Previous
            </Button>
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              {currentStep === 4 ? (
                <Button onClick={handleSave}>
                  <Icon name="Check" size={16} />
                  Create Campaign
                </Button>
              ) : (
                <Button onClick={handleNext}>
                  Next
                  <Icon name="ChevronRight" size={16} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignWizard;